import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AppUpdateDialog } from '@/components/common/AppUpdateDialog';
import { AppUpdateToast } from '@/components/common/AppUpdateToast';
import { useAppVersionCheck } from '@/hooks/useAppVersionCheck';
import { applyAppUpdateReload } from '@/lib/apply-app-update-reload';
import { readAppUpdateUiPreviewParams } from '@/lib/app-update-ui-preview';
import { getLocalAppVersion } from '@/lib/app-version-check';
import { AppUpdateContext } from './app-update-context';

const PREVIEW_REMOTE_VERSION = '9.9.9';
const PREVIEW_CHANGELOG = ['优化了应用性能与稳定性', '修复了已知问题', '改进了用户界面体验'];

export function AppUpdateProvider({ children }: { children: ReactNode }) {
  const state = useAppVersionCheck(applyAppUpdateReload);
  const localVersion = getLocalAppVersion();

  const preview = useMemo(() => readAppUpdateUiPreviewParams(), []);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(preview.active);
  /** TopBar「有新版本」点击后再次显示通知（首次提示由 version 检测驱动） */
  const [notifyOpenFromTopBar, setNotifyOpenFromTopBar] = useState(false);

  useEffect(() => {
    if (!preview.active) return;
    console.info(
      '[jointo] 版本更新 UI 预览：jointoPreviewAppUpdate=1。点「稍后」可关弹窗并看 TopBar 角标；「立即更新」仅日志提示、不刷新。加 &jointoPreviewAppUpdateForce=1 可预览强制更新。去掉 URL 参数后刷新页面即可退出预览。'
    );
  }, [preview.active]);

  const baseDialogOpen = preview.active ? previewDialogOpen : state.dialogOpen;
  const hasNewVersion = preview.active || state.hasNewVersion;
  const notifyOpen = baseDialogOpen || (hasNewVersion && notifyOpenFromTopBar);
  const forceHardRefresh = preview.active ? preview.forceHardRefresh : state.forceHardRefresh;
  const cleanupOnly = preview.active ? preview.cleanupOnly : state.cleanupPending;
  const remoteVersion = preview.active ? PREVIEW_REMOTE_VERSION : state.remoteVersion;
  const previewChangelog = useMemo(
    () => (preview.withChangelog ? PREVIEW_CHANGELOG : []),
    [preview.withChangelog]
  );
  const changelog = preview.active ? previewChangelog : state.changelog;

  const dismissDialogForLater = useCallback(() => {
    setNotifyOpenFromTopBar(false);
    if (preview.active) {
      setPreviewDialogOpen(false);
      return;
    }
    state.dismissDialogForLater();
  }, [preview.active, state]);

  const applyUpdateNow = useCallback(async () => {
    setNotifyOpenFromTopBar(false);
    if (preview.active) {
      console.info(
        '[jointo] 预览模式：未执行清理与刷新。若需看仅 TopBar 状态，可先点「稍后」；退出预览请去掉 URL 参数并刷新。'
      );
      setPreviewDialogOpen(false);
      return;
    }
    await state.applyUpdateNow();
  }, [preview.active, state]);

  const requestUpdateDialog = useCallback(() => {
    if (!hasNewVersion) return;
    setNotifyOpenFromTopBar(true);
  }, [hasNewVersion]);

  const value = useMemo(
    () => ({
      hasNewVersion,
      applyUpdateNow,
      requestUpdateDialog,
      remoteVersion: remoteVersion ?? null,
      forceHardRefresh,
      cleanupOnly,
      changelog,
    }),
    [
      hasNewVersion,
      applyUpdateNow,
      requestUpdateDialog,
      remoteVersion,
      forceHardRefresh,
      cleanupOnly,
      changelog,
    ]
  );

  return (
    <AppUpdateContext.Provider value={value}>
      {children}
      {/* 普通更新：非阻断 Toast，右下角固定 */}
      {!forceHardRefresh && (
        <AppUpdateToast
          open={notifyOpen}
          remoteVersion={remoteVersion}
          changelog={changelog}
          onLater={dismissDialogForLater}
          onNow={applyUpdateNow}
        />
      )}
      {/* 强制更新：Modal 阻断，用户必须刷新 */}
      {forceHardRefresh && (
        <AppUpdateDialog
          open={notifyOpen}
          localVersion={localVersion}
          remoteVersion={remoteVersion}
          forceHardRefresh={forceHardRefresh}
          cleanupOnly={cleanupOnly}
          changelog={changelog}
          onLater={dismissDialogForLater}
          onNow={applyUpdateNow}
        />
      )}
    </AppUpdateContext.Provider>
  );
}
